/**
 * idioma.ts — la parte del idioma que no es React.
 *
 * Está separada del proveedor (`idioma.tsx`) por una razón práctica: Node
 * ejecuta TypeScript directamente pero no sabe de JSX, así que todo lo que se
 * pueda probar sin un navegador vive acá y se prueba de verdad. Lo que queda
 * en el `.tsx` es cableado de React, no reglas.
 */
import { ES, EN, type Clave } from './textos.ts';

export type Idioma = 'es' | 'en';
export type { Clave };

export const DICCIONARIOS = { es: ES, en: EN } as const;

export const LOCALES = { es: 'es-CR', en: 'en-US' } as const;

/**
 * Traduce una clave y sustituye `{nombre}` por lo que se le pase.
 *
 * Si falta la traducción cae al español: es preferible una frase legible en el
 * idioma equivocado que una clave cruda en pantalla. Pero esa caída es un
 * FALLO SILENCIOSO —nadie que pruebe en español lo va a ver nunca—, y por eso
 * `EN` está tipado como `Record<Clave, string>` en textos.ts: la falta se
 * detiene al compilar, mucho antes de que este respaldo haga falta.
 */
export function traducir(
  idioma: Idioma,
  clave: Clave,
  sustituciones?: Readonly<Record<string, string>>,
): string {
  const crudo: string = DICCIONARIOS[idioma][clave] ?? ES[clave] ?? clave;
  if (!sustituciones) return crudo;
  return crudo.replace(/\{(\w+)\}/g, (m, n: string) => sustituciones[n] ?? m);
}

/**
 * Quita las marcas de énfasis y deja el texto plano.
 *
 * Es lo que devuelve `t()`, y por una razón que ya falló una vez: ocho claves
 * del diccionario llevan *asteriscos*, y usar `t()` con una de ellas los
 * pintaba tal cual en la pantalla. No es un fallo que se vea al escribir el
 * componente —hay que abrir esa pantalla concreta, en ese idioma concreto—,
 * así que se cierra por diseño en vez de por disciplina:
 *
 *   `t()`     → siempre texto plano. Sirve en un atributo, un placeholder,
 *               un aria-label o un título, donde un nodo de React no cabe.
 *   `<Texto>` → lo único que dibuja el énfasis.
 *
 * Así ninguna de las dos formas puede ensuciar la pantalla.
 */
export function sinEnfasis(texto: string): string {
  return texto.replace(/\*([^*]+)\*/g, '$1');
}

/**
 * Parte un texto en trozos planos y trozos con *énfasis*.
 *
 * El diccionario del mockup traía `<strong>` porque se inyectaba con
 * `innerHTML`. Acá el énfasis va entre asteriscos y el componente lo convierte
 * en nodos de React, así que ningún texto traducible necesita
 * `dangerouslySetInnerHTML` — una puerta que no conviene abrir en un producto
 * que muestra datos de varios inquilinos.
 */
export function partirEnfasis(texto: string): Array<{ texto: string; fuerte: boolean }> {
  return texto
    .split(/(\*[^*]+\*)/g)
    .filter((p) => p !== '')
    .map((p) =>
      p.length > 2 && p.startsWith('*') && p.endsWith('*')
        ? { texto: p.slice(1, -1), fuerte: true }
        : { texto: p, fuerte: false },
    );
}

/**
 * Una fecha contable, formateada.
 *
 * El mediodía es a propósito. `new Date('2026-08-31')` es medianoche UTC, y en
 * Costa Rica (UTC−6) eso es el día ANTERIOR. Una fecha que se corre un día no
 * avisa de nada: simplemente cae en el periodo equivocado, y el descuadre
 * aparece en el cierre.
 */
export function formatearFecha(iso: string, idioma: Idioma): string {
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(LOCALES[idioma], {
    day: '2-digit', month: 'short', year: 'numeric',
  }).format(d);
}
